/**
 * Builds the engine script for one data tile. Dashboard tiles share one
 * engine session and never RESET, so nothing here may change the data for
 * later tiles: category subsets become a temporary QTABLE of the kept leaves
 * (removed after OUTPUT), never `REMOVE <<Var//Leaf>>`. The tile's result
 * table is removed right after OUTPUT too, because the engine refuses a
 * second XTAB/DTAB into an existing result table name.
 */

import {
  STATISTIC_TYPE,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"
import {
  formatSignificanceClause,
  SIGNIFICANCE_TESTED_STATISTICS,
} from "types/significance.types"
import {
  BuilderSource,
  DataTile,
  UNIVERSE_TOKEN,
} from "features/Dashboard/types/dashboardDocument"
import type { HierarchicalDataEntry } from "types/engineOutput.types"
import { getBoolChildLabels } from "utils/codebookLeaves"
import { UniverseSpec } from "./universeComposer"
import { weightsLine } from "./prepScript"

export interface TileControlState {
  statistic?: STATISTIC_TYPE
  breakdown?: string
  categories?: string[]
}
export type LeavesOf = (variable: string) => string[]

/** The categories of each variable as `codebook` lists them. */
export const leavesOfCodebook =
  (codebook: HierarchicalDataEntry[] | null): LeavesOf =>
  (variable) =>
    getBoolChildLabels(codebook, variable)

/**
 * The result table of tile `tileId`, or of its panel `panel` (counted from
 * 1) when the tile repeats. Each panel has its own, so a panel whose run
 * failed leaves nothing behind that another panel's run would trip over.
 */
export const tileTableId = (tileId: string, panel?: number): string => {
  const id = `t_${tileId.replace(/[^A-Za-z0-9_]/g, "_")}`
  return panel === undefined ? id : `${id}_${panel}`
}

/**
 * `script` with the table name `from` renamed to `to` wherever it stands
 * alone or ends a longer name (the `__dq_` temporaries of "Switch to
 * script" carry it too). Table ids hold only letters, digits and "_", so
 * they need no escaping.
 */
const renameTable = (script: string, from: string, to: string): string =>
  script.replace(new RegExp(`${from}(?![A-Za-z0-9_])`, "g"), to)

/**
 * `script` with the result table name of tile `fromId` renamed to that of
 * `toId` (see `renameTable`). A duplicated script tile otherwise writes
 * into, and removes, its original's table.
 */
export const retargetScript = (
  script: string,
  fromId: string,
  toId: string
): string => renameTable(script, tileTableId(fromId), tileTableId(toId))

/** First entry of `removed.rows` that turns the list into "keep only these". */
export const KEEP_ONLY = "__keep_only__"

/**
 * `source` with the viewer's choices applied. A Statistic pick narrows the
 * run to that statistic, plus any of `keep` the source computes (a rolling
 * average keeps its base, "n").
 */
export const applyControls = (
  source: BuilderSource,
  controls?: TileControlState,
  keep: STATISTIC_TYPE[] = []
): BuilderSource => {
  if (!controls) return source
  const next: BuilderSource = {
    ...source,
    removed: {
      rows: [...source.removed.rows],
      columns: [...source.removed.columns],
    },
  }
  if (controls.statistic) {
    const picked = controls.statistic
    next.statistics = [
      picked,
      ...keep.filter((s) => s !== picked && source.statistics.includes(s)),
    ]
  }
  if (controls.breakdown) {
    next.columns = [controls.breakdown, ...source.columns.slice(1)]
    // The removals belong to the old breakdown; bare ids would otherwise
    // drop same-label categories of the new one.
    next.removed.columns = []
  }
  if (
    controls.categories &&
    controls.categories.length > 0 &&
    source.rows.length === 1
  ) {
    next.removed.rows = [KEEP_ONLY, ...controls.categories]
  }
  return next
}

const lastSegment = (path: string): string => path.split("//").pop() ?? path

/**
 * The leaves of `variable` that stay after `removed` (leaf ids as the table
 * model reports them: "Male", or "Gender//Male" on a banner). `null` means
 * nothing is removed for this variable, so the variable is used whole.
 */
const keptLeaves = (
  variable: string,
  removed: string[],
  leavesOf: LeavesOf
): string[] | null => {
  const keepOnly = removed[0] === KEEP_ONLY
  const ids = keepOnly ? removed.slice(1) : removed
  const seg = lastSegment(variable)
  const mine = ids
    .filter((id) => !id.includes("//") || id.startsWith(`${seg}//`))
    .map((id) => (id.includes("//") ? id.slice(seg.length + 2) : id))
  if (mine.length === 0) return null
  const all = leavesOf(variable)
  if (all.length === 0) return null
  const set = new Set(mine)
  return keepOnly
    ? all.filter((l) => set.has(l))
    : all.filter((l) => !set.has(l))
}

interface AxisExpr {
  declarations: string[]
  expression: string
  temps: string[]
}

const axisExpression = (
  variables: string[],
  removed: string[],
  tempName: string,
  leavesOf: LeavesOf
): AxisExpr => {
  if (variables.length === 0) {
    return {
      declarations: [`QTABLE ${tempName}`],
      expression: tempName,
      temps: [tempName],
    }
  }
  const subsets = variables.map((v) => keptLeaves(v, removed, leavesOf))
  const needsTemp = variables.length > 1 || subsets.some((s) => s !== null)
  if (!needsTemp) {
    return { declarations: [], expression: `<<${variables[0]}>>`, temps: [] }
  }
  const declarations = [`QTABLE ${tempName}`]
  variables.forEach((v, i) => {
    const subset = subsets[i]
    if (subset === null) {
      declarations.push(`APPEND ${tempName} <<${v}>>`)
    } else {
      subset.forEach((leaf) =>
        declarations.push(`APPEND ${tempName} <<${v}//${leaf}>>`)
      )
    }
  })
  return { declarations, expression: tempName, temps: [tempName] }
}

const operatorsClause = (source: BuilderSource): string => {
  const sig = formatSignificanceClause(source.significance)
  const ops = [...source.statistics]
  if (sig) {
    const tested = SIGNIFICANCE_TESTED_STATISTICS[source.operation] ?? []
    tested.forEach((s) => {
      if (!ops.includes(s)) ops.push(s)
    })
  }
  const list = ops.map((o) => `"${o}"`).join(",")
  return `${ops.length > 0 ? ` OPERATORS: ${list}` : ""}${sig}`
}

export const buildBuilderScript = (args: {
  tileId: string
  source: BuilderSource
  universeClause: string
  transposed?: boolean
  leavesOf: LeavesOf
  /** The panel this script runs for: its tables carry the panel's number. */
  panel?: number
}): string => {
  const { source } = args
  const id = tileTableId(args.tileId, args.panel)
  const rows = axisExpression(
    source.rows,
    source.removed.rows,
    `__dq_r_${id}`,
    args.leavesOf
  )
  const cols = axisExpression(
    source.columns,
    source.removed.columns,
    `__dq_e_${id}`,
    args.leavesOf
  )
  const op = source.operation === TABLE_OPERATION_TYPE.DTAB ? "DTAB" : "XTAB"
  const lines = [
    ...rows.declarations,
    ...cols.declarations,
    `${op} ${id} ${rows.expression} ${cols.expression}${args.universeClause}${operatorsClause(source)}`,
    `OUTPUT ${id}${args.transposed ? " /T" : ""}`,
    // OUTPUT has already copied the table, so the name is free for the next run.
    `REMOVE ${id}`,
    ...[...rows.temps, ...cols.temps].map((t) => `REMOVE ${t}`),
  ]
  return lines.join("\n")
}

/**
 * The builder script with ` {{universe}}` where the universe clause goes,
 * for "Switch to script". `transposed` keeps a table tile's `/T`.
 */
export const builderSourceToScriptText = (args: {
  tileId: string
  source: BuilderSource
  transposed?: boolean
  leavesOf: LeavesOf
}): string =>
  buildBuilderScript({ ...args, universeClause: ` ${UNIVERSE_TOKEN}` })

/** The dashboard weight, and whether this tile runs without it. */
export interface TileWeight {
  active: string | null
  unweighted: boolean
}

/**
 * `script` behind its own weight line while the dashboard has a weight:
 * `USEWEIGHTS <<weight>>` for a weighted tile, a bare `USEWEIGHTS` for an
 * unweighted one. No script relies on the weight an earlier script left in
 * the session, so a run that stops part way (the engine stops at the
 * failing line) never changes the weighting of the scripts queued after
 * it. Without a dashboard weight the script is left as it is.
 */
const withTileWeight = (script: string, weight?: TileWeight): string =>
  weight?.active
    ? `${weightsLine(weight.unweighted ? null : weight.active)}\n${script}`
    : script

/**
 * The script to run for `tile`. In a script tile `{{universe}}` becomes
 * `UNIVERSE: <<field>>` (the author supplies the space before the token), or
 * nothing when there is no universe. `emitPrefix` puts the universe
 * declaration in front of the script. While the dashboard has a weight,
 * the script starts with its own weight line (see `withTileWeight`). A
 * `panel` (counted from 1) names the result table and temporaries after
 * that panel of a repeated tile: a builder tile builds them so, and a
 * script tile's own table name (`t_<tile>`, as "Switch to script" writes
 * it) is renamed to the panel's. Other names in a script are its author's.
 */
export const buildTileScript = (args: {
  tile: DataTile
  universe: UniverseSpec | null
  emitPrefix: boolean
  controls?: TileControlState
  leavesOf: LeavesOf
  /** Statistics a Statistic pick does not drop (see `applyControls`). */
  keepStatistics?: STATISTIC_TYPE[]
  weight?: TileWeight
  panel?: number
}): { script: string; usesUniverse: boolean; outputIndex: number } => {
  const clause = args.universe?.clause ?? ""
  const prefix =
    args.universe && args.emitPrefix ? `${args.universe.prefix}\n` : ""
  if (args.tile.source.kind === "script") {
    const usesUniverse = args.tile.source.script.includes(UNIVERSE_TOKEN)
    const own = args.tile.source.script
      .split(UNIVERSE_TOKEN)
      .join(clause.trimStart())
    // Each panel writes its own tables, so a failed panel's leftovers never
    // trip another panel's run.
    const body =
      args.panel === undefined
        ? own
        : renameTable(
            own,
            tileTableId(args.tile.id),
            tileTableId(args.tile.id, args.panel)
          )
    return {
      script: withTileWeight(`${prefix}${body}`, args.weight),
      usesUniverse,
      outputIndex: args.tile.source.outputIndex,
    }
  }
  const source = applyControls(
    args.tile.source,
    args.controls,
    args.keepStatistics
  )
  const transposed =
    args.tile.type === "table" ? args.tile.visual.transposed : false
  const body = buildBuilderScript({
    tileId: args.tile.id,
    source,
    universeClause: clause,
    transposed,
    leavesOf: args.leavesOf,
    panel: args.panel,
  })
  return {
    script: withTileWeight(`${prefix}${body}`, args.weight),
    usesUniverse: true,
    outputIndex: 0,
  }
}
